<template>
    <div>
        <Head title="Bookings"/>
        <h1 class="mb-8 text-3xl font-bold">Biglietti</h1>

        <!-- Inline Filters -->
        <div class="mb-6 p-4 bg-white rounded-lg shadow">
            <div class="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                <!-- Search -->
                <div>
                    <label class="block text-gray-700 text-sm font-medium mb-1">Cerca</label>
                    <input
                        v-model="form.search"
                        type="text"
                        placeholder="Nome, email, ID..."
                        class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    />
                </div>

                <!-- Start Date -->
                <div>
                    <label class="block text-gray-700 text-sm font-medium mb-1">Data Visita Inizio</label>
                    <DatePicker v-model="form.start_date" :masks="{ input: 'YYYY-MM-DD' }"
                                :popover-props="{ visibility: 'click' }" locale="it">
                        <template #default="{ inputValue, inputEvents }">
                            <input
                                :value="inputValue"
                                placeholder="Seleziona data"
                                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                                v-on="inputEvents"
                            />
                        </template>
                    </DatePicker>
                </div>

                <!-- End Date -->
                <div>
                    <label class="block text-gray-700 text-sm font-medium mb-1">Data Visita Fine</label>
                    <DatePicker v-model="form.end_date" :masks="{ input: 'YYYY-MM-DD' }"
                                :popover-props="{ visibility: 'click' }" locale="it">
                        <template #default="{ inputValue, inputEvents }">
                            <input
                                :value="inputValue"
                                placeholder="Seleziona data"
                                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                                v-on="inputEvents"
                            />
                        </template>
                    </DatePicker>
                </div>

                <!-- Payment Status -->
                <div>
                    <label class="block text-gray-700 text-sm font-medium mb-1">Stato Pagamento</label>
                    <select
                        v-model="form.payment_status"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    >
                        <option :value="null">Tutti (solo pagati)</option>
                        <option :value="''">Tutti</option>
                        <option v-for="(label, status) in paymentStatuses" :key="status" :value="status">
                            {{ label }}
                        </option>
                    </select>
                </div>

                <!-- Reset Button -->
                <div class="flex items-end">
                    <button
                        @click="reset"
                        class="w-full px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md shadow-sm"
                    >
                        Reset Filtri
                    </button>
                </div>
            </div>
        </div>

        <!-- Scanner Button and Total -->
        <div class="flex items-center justify-between mb-6">
            <div class="flex flex-col">
                <p class="text-lg font-semibold">Totale Pagato: {{ getTotals }}</p>
                <p v-if="form.start_date || form.end_date" class="text-sm text-gray-600">
                    {{ form.start_date ? formatDate(form.start_date) : '...' }} - {{ form.end_date ? formatDate(form.end_date) : '...' }}
                </p>
            </div>
            <button class="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded" @click="openModal">
                Apri scanner
            </button>
            <Modal :is-open="isModalOpen" @close="closeModal" @validate="validateBooking"/>
        </div>

        <div class="relative overflow-x-auto shadow-md sm:rounded-lg">
            <table class="w-full text-sm text-left rtl:text-right text-gray-500">
                <thead class="text-xs text-gray-700 uppercase bg-gray-50">
                <tr>
                    <th @click="sort('id')" class="px-6 py-3 cursor-pointer hover:bg-gray-300" scope="col">ID
<!--                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-5">-->
<!--                        <path fillRule="evenodd" d="M2 3.75A.75.75 0 0 1 2.75 3h11.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 3.75ZM2 7.5a.75.75 0 0 1 .75-.75h7.508a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 7.5ZM14 7a.75.75 0 0 1 .75.75v6.59l1.95-2.1a.75.75 0 1 1 1.1 1.02l-3.25 3.5a.75.75 0 0 1-1.1 0l-3.25-3.5a.75.75 0 1 1 1.1-1.02l1.95 2.1V7.75A.75.75 0 0 1 14 7ZM2 11.25a.75.75 0 0 1 .75-.75h4.562a.75.75 0 0 1 0 1.5H2.75a.75.75 0 0 1-.75-.75Z" clipRule="evenodd" />-->
<!--                    </svg>-->
<!--                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-5">-->
<!--                            <path fillRule="evenodd" d="M2 3.75A.75.75 0 0 1 2.75 3h11.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 3.75ZM2 7.5a.75.75 0 0 1 .75-.75h6.365a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 7.5ZM14 7a.75.75 0 0 1 .55.24l3.25 3.5a.75.75 0 1 1-1.1 1.02l-1.95-2.1v6.59a.75.75 0 0 1-1.5 0V9.66l-1.95 2.1a.75.75 0 1 1-1.1-1.02l3.25-3.5A.75.75 0 0 1 14 7ZM2 11.25a.75.75 0 0 1 .75-.75H7A.75.75 0 0 1 7 12H2.75a.75.75 0 0 1-.75-.75Z" clipRule="evenodd" />-->
<!--                        </svg>-->
                    </th>
                    <th @click="sort('name')" class="px-6 py-3 cursor-pointer hover:bg-gray-300" scope="col">Nome</th>
                    <th @click="sort('pass_date')" class="px-6 py-3 cursor-pointer hover:bg-gray-300" scope="col">Data</th>
                    <th @click="sort('created_at')" class="px-6 py-3 cursor-pointer hover:bg-gray-300" scope="col">Data Acquisto</th>
                    <th class="px-6 py-3" scope="col">Ombrelloni</th>
                    <th @click="sort('paid_amount')" class="px-6 py-3 cursor-pointer hover:bg-gray-300" scope="col">Totale</th>
                    <th class="px-6 py-3" scope="col">Validato</th>
                    <th class="px-6 py-3" scope="col">Stato Email</th>
                    <th class="px-6 py-3" scope="col">Azioni</th>
                </tr>
                </thead>

                <tbody>
                <tr v-for="booking in bookings.data" :key="booking.id"
                    class="odd:bg-white even:bg-gray-50 border-b">
                    <td class="px-6 py-4 text-gray-900 whitespace-nowrap" scope="row">
                        {{ booking.id }}
                    </td>
                    <td class="px-6 py-4 text-gray-900 whitespace-nowrap" scope="row">
                        {{ booking.first_name }} {{ booking.last_name }}
                        <br>
                        {{ booking.email }}
                    </td>
                    <td class="px-6 py-4 text-gray-900 whitespace-nowrap" scope="row">
                        {{ booking.pass_date }}<br>
                    </td>
                    <td class="px-6 py-4 text-gray-900 whitespace-nowrap" scope="row">
                        {{ booking.created_at }}<br>
                    </td>
                    <td class="px-6 py-4 text-gray-900 whitespace-nowrap" scope="row">
                        <div v-for="(station, index) in booking.stations" :key="index">
                          <div class="p-2">
                            <p>Ombrellone {{ station.id }} (Lettini {{ station.sunbedCount }})</p>
                            <p>Extra lettini: {{ station.extraSunbedCount ?? 0 }}</p>
                            <p>Extra adulti: {{ station.extraAdultCount ?? 0 }}</p>
                          </div>
                        </div>
                        <p v-if="booking.qty_adults" class="p-2">Adulti: {{ booking.qty_adults }}</p>
                        <p v-if="booking.qty_kids" class="p-2">Bambini: {{ booking.qty_kids }}</p>
                    </td>
                    <td class="px-6 py-4 text-gray-900 whitespace-nowrap" scope="row">
                        {{ booking.paid_amount }} €
                    </td>
                    <td class="px-6 py-4 text-gray-900 whitespace-nowrap" scope="row">
                        {{ booking.validated ? 'Si' : 'No' }}
                    </td>
                    <td class="px-6 py-4 text-gray-900 whitespace-nowrap" scope="row">
                        <span :class="getEmailStatusClass(booking.email_status)">
                            {{ booking.email_status.label }}
                            <span v-if="booking.email_status.retry_count > 0" class="text-xs">
                                ({{ booking.email_status.retry_count }}/3)
                            </span>
                        </span>
                    </td>
                    <td class="text-gray-900 whitespace-nowrap flex items-center justify-center space-x-1 table-cell" scope="row">
                        <a :href="'/bookings/' + booking.id + '/edit'" class="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm p-1.5 text-center inline-flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-pencil"><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"/><path d="m15 5 4 4"/></svg>
                        </a>

                        <button
                            class="text-white bg-yellow-700 hover:bg-yellow-800 focus:ring-4 focus:outline-none focus:ring-yellow-300 font-medium rounded-lg text-sm p-1.5 text-center inline-flex items-center"
                            type="button"
                            @click="resendEmail(booking.id)"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-mail-check"><path d="M22 13V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h8"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/><path d="m16 19 2 2 4-4"/></svg>
                        </button>

                        <button
                            v-if="!booking.validated"
                            class="text-white bg-green-700 hover:bg-green-800 focus:ring-4 focus:outline-none focus:ring-green-300 font-medium rounded-lg text-sm p-1.5 text-center inline-flex items-center"
                            type="button"
                            @click="validateBooking(booking.id)"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-square-check-big"><path d="m9 11 3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                        </button>
                    </td>
                </tr>
                <tr v-if="bookings.length === 0">
                    <td class="px-6 py-4 border-t" colspan="9">Nessuna prenotazione trovata.</td>
                </tr>
                </tbody>
            </table>
        </div>
      <pagination class="mt-6" :links="bookings.links" />
    </div>
</template>

<script>
import Modal from "@/Pages/Bookings/BookingScanner.vue";
import DashboardLayout from "@/Pages/Dashboard/Shared/DashboardLayout";
import Icon from "@/Pages/Dashboard/Shared/Icon";
import Pagination from "@/Pages/Dashboard/Shared/Pagination";
import { Head, Link } from "@inertiajs/inertia-vue3";
import axios from "axios";
import throttle from "lodash/throttle";
import moment from "moment";
import { DatePicker } from "v-calendar";
import { ref } from "vue";
import { useToast } from "vue-toastification";

export default {
  components: {
    DatePicker,
    Head,
    Icon,
    Link,
    Pagination,
    Modal,
  },
  layout: DashboardLayout,
  props: {
    filters: Object,
    bookings: Object,
    paymentStatuses: Object,
  },
  setup() {
    const isModalOpen = ref(false);

    const openModal = () => {
      isModalOpen.value = true;
    };

    const closeModal = () => {
      isModalOpen.value = false;
    };

    const today = moment().format("YYYY-MM-DD");

    return {
      isModalOpen,
      openModal,
      closeModal,
      today,
    };
  },
  data() {
    return {
      form: {
        search: this.filters.search || "",
        start_date: this.filters.start_date || null,
        end_date: this.filters.end_date || null,
        payment_status: this.filters.payment_status !== undefined ? this.filters.payment_status : null,
        sortField: "pass_date",
        sortDirection: "desc",
      },
    };
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.loadBookings();
      }, 150),
    },
  },
  computed: {
    getTotals() {
      const total = this.bookings.data
        .filter((booking) => booking.payment_status === 1)
        .reduce((total, booking) => {
          return total + booking.paid_amount;
        }, 0);

      // Format the total to Italian currency
      return new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
      }).format(total);
    },
  },
  methods: {
    reset() {
      this.form.search = "";
      this.form.start_date = null;
      this.form.end_date = null;
      this.form.payment_status = null;
      this.loadBookings();
    },
    formatDate(date) {
      return moment(date).format('DD/MM/YYYY');
    },
    sort(field) {
      this.sortDirection =
        this.sortField === field && this.sortDirection === "desc"
          ? "asc"
          : "desc";
      this.sortField = field;
      this.loadBookings();
    },
    async validateBooking(id, token = null) {
      const toast = useToast();
      try {
        await axios.post(`/bookings/${id}/validate`, {
          token: token,
        });
        toast.success("Biglietto convalidato con successo!");
        await this.loadBookings();
      } catch (error) {
        console.error("Error submitting form:", error);
        toast.error("Impossibile convalidare il biglietto");
      }
    },
    async resendEmail(id) {
      const toast = useToast();
      if (
        !confirm(
          "Sei sicuro di voler rimandare la mail di conferma del'ordine al cliente?",
        )
      ) {
        return false;
      }
      try {
        await axios.post(`/bookings/${id}/receipt`).then(() => {
          toast.success("Biglietto rimandato!");
        });

        await this.loadBookings();
      } catch (error) {
        console.error("Error submitting form:", error);
        toast.error("Impossibile rimandare la mail");
      }
    },
    async loadBookings() {
      try {
        const params = {
          search: this.form.search || null,
          start_date: this.form.start_date ? moment(this.form.start_date).format("YYYY-MM-DD") : null,
          end_date: this.form.end_date ? moment(this.form.end_date).format("YYYY-MM-DD") : null,
          payment_status: this.form.payment_status !== null && this.form.payment_status !== undefined ? this.form.payment_status : null,
          sortField: this.sortField,
          sortDirection: this.sortDirection,
        };

        this.$inertia.get("/bookings", params, {
          preserveState: true,
        });
      } catch (error) {
        console.error("Error loading bookings:", error);
      }
    },
    getEmailStatusClass(emailStatus) {
      if (!emailStatus || emailStatus.status === 'none') {
        return 'px-2 py-1 text-xs rounded bg-gray-100 text-gray-800';
      }

      const statusClasses = {
        'pending': 'px-2 py-1 text-xs rounded bg-yellow-100 text-yellow-800',
        'sent': 'px-2 py-1 text-xs rounded bg-blue-100 text-blue-800',
        'delivered': 'px-2 py-1 text-xs rounded bg-green-100 text-green-800',
        'failed': 'px-2 py-1 text-xs rounded bg-red-100 text-red-800',
        'bounced': 'px-2 py-1 text-xs rounded bg-red-100 text-red-800',
        'complained': 'px-2 py-1 text-xs rounded bg-orange-100 text-orange-800',
        'opened': 'px-2 py-1 text-xs rounded bg-green-100 text-green-800',
      };

      return statusClasses[emailStatus.status] || 'px-2 py-1 text-xs rounded bg-gray-100 text-gray-800';
    },
  },
};
</script>

<style scoped>
@media (min-width: 640px) {
    table {
        display: inline-table !important;
    }

    thead tr:not(:first-child) {
        display: none;
    }
}

td:not(:last-child) {
    border-bottom: 0;
}

th:not(:last-child) {
    border-bottom: 2px solid rgba(0, 0, 0, .1);
}
</style>
